<template>
  <div class="super-secretary-toggle">
    <f-checkbox
      id="super-secretary"
      label="Super Secretary"
      hint="Can switch between committees and work on their behalf."
      :value="checked"
      :disabled="processing"
      @input="save"
    >
      <!-- Inside the checkbox <label>: .prevent.stop so opening the doc never toggles the box -->
      <button
        slot="after-label"
        type="button"
        class="super-secretary-help ml-2"
        aria-label="What is a Super Secretary?"
        title="What is a Super Secretary?"
        @click.prevent.stop="showDoc = true"
      >?</button>
    </f-checkbox>
    <doc-modal v-if="showDoc" doc="secretaries.super" @close="showDoc = false" />
    <transition name="fade">
      <div v-if="inlineMessage" :key="inlineMessage.id" :class="['alert mb-0 alert-pop', `alert-${inlineMessage.type}`]" role="alert" title="Click to dismiss" style="cursor: pointer" @click="inlineMessage = null">
        {{ inlineMessage.text }}
      </div>
    </transition>
  </div>
</template>

<script>
import FCheckbox from '@/components/FormFields/FCheckbox'
import DocModal from '@/components/Docs/DocModal'
import EventBus from '@/store/EventBus'
import secretariesApi from '@/services/api/secretaries'

// Admin-only (CAM-1533). Persists on change via its own endpoint, independent of the profile
// form, and reports the outcome through the page's AppAlert (EventBus show-success / http-error).
export default {
  name: 'SuperSecretaryToggle',
  components: { FCheckbox, DocModal },
  props: {
    memberId: { type: [Number, String], required: true },
    value: { type: Boolean, default: false },
    // Show the result right of the checkbox instead of the page-level AppAlert
    inlineAlert: { type: Boolean, default: false }
  },
  data() {
    return { checked: this.value, processing: false, inlineMessage: null, messageTimer: null, messageSeq: 0, showDoc: false }
  },
  watch: {
    value(v) { this.checked = v }
  },
  beforeDestroy() {
    clearTimeout(this.messageTimer)
  },
  methods: {
    notify(type, text) {
      if (!this.inlineAlert) {
        EventBus.$emit(type === 'success' ? 'show-success' : 'http-error', text)
        return
      }
      clearTimeout(this.messageTimer)
      this.inlineMessage = { type, text, id: ++this.messageSeq }
      this.messageTimer = setTimeout(() => { this.inlineMessage = null }, 5000)
    },
    save(checked) {
      this.processing = true
      // Optimistic: the box reflects the click while the request is in flight; reverted on failure
      this.checked = checked
      secretariesApi.setSuperSecretary(this.memberId, checked).then((response) => {
        this.$emit('input', checked)
        this.notify('success', response.data.message || 'Super Secretary updated.')
      }).catch((error) => {
        this.checked = !checked
        const data = error.response && error.response.data
        this.notify('warning', (data && data.message) || 'Unable to update Super Secretary. Please try again.')
      }).finally(() => { this.processing = false })
    }
  }
}
</script>

<style scoped>
/* Same two-column split as the form grid (.f-form__half): checkbox in the left half, result in the right half */
.super-secretary-toggle {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 1.5rem;
  row-gap: 0.75rem;
  align-items: center;
}

/* Small round "?" next to the label — opens the Super Secretary doc */
.super-secretary-help {
  flex: none;
  width: 1rem;
  height: 1rem;
  position: relative;
  top: -2px;
  vertical-align: middle;
  padding: 0;
  border: 1px solid var(--primary, #aa2d41);
  border-radius: 50%;
  background: transparent;
  color: var(--primary, #aa2d41);
  font-size: 0.65rem;
  font-weight: bold;
  line-height: 1;
  cursor: pointer;
}

.super-secretary-help:hover {
  background: var(--primary, #aa2d41);
  color: #fff;
}

/* Suppress the browser/Bootstrap blue focus ring and tap highlight on click */
.super-secretary-help,
.super-secretary-help:focus,
.super-secretary-help:active {
  outline: none;
  box-shadow: none;
  -webkit-tap-highlight-color: transparent;
}

.super-secretary-help:focus,
.super-secretary-help:active {
  border-color: var(--primary, #aa2d41);
  background: transparent;
  color: var(--primary, #aa2d41);
}

.super-secretary-help:hover:focus,
.super-secretary-help:hover:active {
  background: var(--primary, #aa2d41);
  color: #fff;
}

@media (max-width: 575.98px) {
  .super-secretary-toggle {
    grid-template-columns: 1fr;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>



// WEBPACK FOOTER //
// src/components/Members/SuperSecretaryToggle.vue